import { useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@multaqa/ui/components/ui/dropdown-menu'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { MoreHorizontalIcon, PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { localizedText } from '@/lib/localized-text'
import { type PositionResource, useCompanyPositions } from './api'
import { CompanyPositionDialog } from './company-position-dialog'

export function CompanyPositions({ groupId, companyId }: { groupId: string; companyId: string }) {
  const { t } = useTranslation()
  const positions = useCompanyPositions(groupId, companyId)
  const messages = useAppErrorMessages()
  const [editing, setEditing] = useState<PositionResource | null | 'new'>(null)
  if (positions.isLoading) return <ListSkeleton columns={[2, 1, 1, 0.75, 0.5]} rows={6} />
  if (positions.isError)
    return <AppError error={positions.error} messages={messages} retry={positions.refetch} />

  const list = positions.data ?? []
  return (
    <section data-testid="company-positions" aria-labelledby="company-positions-heading">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 id="company-positions-heading" className="text-h3">
            {t('companies.positions.title')}
          </h2>
          <p className="text-small text-muted-foreground">{t('companies.positions.subtitle')}</p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => setEditing('new')}
          data-testid="company-positions-add"
        >
          <PlusIcon className="size-3.5" aria-hidden="true" />
          {t('companies.positions.add')}
        </Button>
      </div>
      {list.length === 0 ? (
        <EmptyState
          title={t('companies.positions.empty')}
          description={t('companies.positions.emptyDescription')}
          action={
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing('new')}>
              {t('companies.positions.add')}
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <Table
            className="min-w-[620px] border border-border"
            data-testid="company-positions-table"
          >
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead>{t('companies.positions.nameEn')}</TableHead>
                <TableHead>{t('companies.positions.nameAr')}</TableHead>
                <TableHead>{t('companies.positions.code')}</TableHead>
                <TableHead>{t('companies.positions.status')}</TableHead>
                <TableHead className="text-end">{t('companies.positions.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((position) => (
                <PositionRow key={position.id} position={position} onEdit={setEditing} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {editing !== null ? (
        <CompanyPositionDialog
          key={editing === 'new' ? 'new' : editing.id}
          position={editing === 'new' ? null : editing}
          groupId={groupId}
          companyId={companyId}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        />
      ) : null}
    </section>
  )
}

function PositionRow({
  position,
  onEdit,
}: {
  position: PositionResource
  onEdit: (position: PositionResource) => void
}) {
  const { t } = useTranslation()
  return (
    <TableRow data-testid={`company-position-row-${position.id}`}>
      <TableCell>{localizedText(position.name, 'en')}</TableCell>
      <TableCell dir="rtl">{localizedText(position.name, 'ar')}</TableCell>
      <TableCell className="text-identifier font-mono">{position.code}</TableCell>
      <TableCell>
        <Badge tone={position.is_active ? 'success' : 'neutral'}>
          {t(`companies.positions.${position.is_active ? 'active' : 'inactive'}`)}
        </Badge>
      </TableCell>
      <TableCell className="text-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('companies.positions.rowActions', { code: position.code })}
              data-testid={`company-position-actions-${position.id}`}
            >
              <MoreHorizontalIcon className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onEdit(position)}
              data-testid={`company-position-edit-${position.id}`}
            >
              {t('companies.positions.edit')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}
